<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <!--
    Bind :innerHTML rather than the v-html directive: v-html is dropped on a dynamic
    <component :is> during SSR, so the element would render empty server-side. innerHTML
    is a plain prop that the SSR serialiser renders. Content is trusted HTML from ~/utils/md.
  -->
  <!-- eslint-disable vue/attribute-hyphenation -- innerHTML must stay camelCase; the hyphenated form does not map to the DOM property and breaks SSR -->
  <component
    :is="as"
    class="space-y-4 [&_ul]:ml-7 [&_ul]:list-disc"
    :role="as === 'a' ? undefined : isHeading ? 'heading' : 'group'"
    :aria-level="isHeading ? headingLevel : undefined"
    :innerHTML="content"
  />
</template>

<script setup lang="ts">
import { renderMarkdownBlock, renderMarkdownInAnchor, renderMarkdownInline } from '~/utils/md'

type MarkdownAs = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'div' | 'p' | 'a' | 'span'

const props = defineProps<{
  text: string | null | undefined
  as: MarkdownAs
  shouldStripLinks?: boolean
}>()

const isAnchor = computed(() => props.as === 'a')
const isHeading = computed(() => props.as?.startsWith('h') ?? false)
const isInlineElement = computed(() => props.as === 'span' || props.as === 'p' || isHeading.value)
const headingLevel = computed(() => (isHeading.value ? Number(props.as.slice(1)) : undefined))

const content = computed(() => {
  const text = props.text ?? ''
  const allowLinks = !props.shouldStripLinks

  if (isAnchor.value) return renderMarkdownInAnchor(text)
  if (isInlineElement.value) return renderMarkdownInline(text, { allowLinks })

  return renderMarkdownBlock(text, { allowLinks })
})
</script>
